<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAdmin } from '@/stores/admin'
import { useImports, type PlatformImportStats } from '@/stores/imports'
import type {
  PlatformSubscription,
  CountrySubscription,
  BrandSubscription,
  PlatformAdvertiser,
  Brand,
  Sector,
} from '@/stores/admin'
import { handleError } from '@/lib/error'
import { formatPlatformName } from '@/lib/utils'
import AdminBrandForm from '@/views/forms/AdminBrandForm.vue'
import AdminBrandDetail from '@/views/AdminBrandDetail.vue'
import AdminSectorDetail from '@/views/AdminSectorDetail.vue'
import BrandsTable from '@/components/BrandsTable.vue'
import SegmentedControl from '@/components/SegmentedControl.vue'
import TriStateFilterButtons from '@/components/TriStateFilterButtons.vue'
import { useTriStateFilter, NO_SECTOR } from '@/composables/useTriStateFilter'
import { activeFilterUi } from '@/lib/filterUtils'

const viewModeOptions = [
  { value: 'buttons', icon: 'i-lucide-grid' },
  { value: 'table', icon: 'i-lucide-list' },
]

const route = useRoute()
const router = useRouter()
const admin = useAdmin()
const imports = useImports()

const isLoading = ref(true)
const isSaving = ref(false)
const organization = computed(
  () => admin.organizations.find((o) => o.slug === (route.params.slug as string)) ?? null,
)

// Search/filter states
const brandSearchQuery = ref('')
const countrySearchQuery = ref('')
const showOnlySelectedBrands = ref(true)
const showOnlySelectedCountries = ref(true)
const showOnlySelectedPlatforms = ref(true)

// Brands view mode (buttons or table)
const brandsViewMode = ref<'buttons' | 'table'>('buttons')
const brandAdvertisersMap = ref<Map<string, PlatformAdvertiser[]>>(new Map())
const brandImportStatsMap = ref<Map<string, PlatformImportStats[]>>(new Map())
const isLoadingTableData = ref(false)
const tableDataLoaded = ref(false)

// Brand creation modal and detail slideover
const showCreateBrandModal = ref(false)
const showBrandSlideover = ref(false)
const selectedBrand = ref<Brand | null>(null)

// Sector detail slideover
const showSectorSlideover = ref(false)
const selectedSector = ref<Sector | null>(null)

// Subscriptions
const platformSubs = ref<PlatformSubscription[]>([])
const countrySubs = ref<CountrySubscription[]>([])
const brandSubs = ref<BrandSubscription[]>([])

// Selected IDs
const selectedPlatformIds = ref<string[]>([])
const selectedCountryIds = ref<string[]>([])
const selectedBrandIds = ref<string[]>([])

// Hidden IDs (is_visible = false)
const hiddenPlatformIds = ref<string[]>([])
const hiddenCountryIds = ref<string[]>([])
const hiddenBrandIds = ref<string[]>([])

// Owned brand IDs (org brands vs tracked brands)
const ownedBrandIds = ref<string[]>([])

// Expanded platforms (to show subplatforms)
const expandedPlatformIds = ref<string[]>([])

// Brand sector filter
const sectorFilter = useTriStateFilter()

// Brand platform filter (cycles: none -> excluded/missing -> included/has -> none)
const platformFilter = useTriStateFilter()

onMounted(async () => {
  window.addEventListener('hashchange', handleHashChange)

  if (admin.organizations.length === 0) {
    await admin.fetchOrganizations()
  }

  if (!organization.value) {
    isLoading.value = false
    return
  }

  // Fetch reference data if needed
  await Promise.all([
    admin.platforms.length === 0 ? admin.fetchPlatforms() : Promise.resolve(),
    admin.countries.length === 0 ? admin.fetchCountries() : Promise.resolve(),
    admin.countryGroups.length === 0 ? admin.fetchCountryGroups() : Promise.resolve(),
    admin.brands.length === 0 ? admin.fetchBrands() : Promise.resolve(),
    admin.sectors.length === 0 ? admin.fetchSectors() : Promise.resolve(),
  ])

  // Fetch subscriptions (separate Promise.all for type safety)
  const [platformSubsResult, countrySubsResult, brandSubsResult] = await Promise.all([
    admin.fetchOrgPlatformSubscriptions(organization.value.id),
    admin.fetchOrgCountrySubscriptions(organization.value.id),
    admin.fetchOrgBrandSubscriptions(organization.value.id),
  ])

  platformSubs.value = platformSubsResult
  countrySubs.value = countrySubsResult
  brandSubs.value = brandSubsResult

  selectedPlatformIds.value = platformSubs.value.map((s) => s.platform_id)
  selectedCountryIds.value = countrySubs.value.map((s) => s.country_id)
  selectedBrandIds.value = brandSubs.value.map((s) => s.brand_id)
  ownedBrandIds.value = brandSubs.value.filter((s) => s.is_owner).map((s) => s.brand_id)
  hiddenPlatformIds.value = platformSubs.value
    .filter((s) => !s.is_visible)
    .map((s) => s.platform_id)
  hiddenCountryIds.value = countrySubs.value.filter((s) => !s.is_visible).map((s) => s.country_id)
  hiddenBrandIds.value = brandSubs.value.filter((s) => !s.is_visible).map((s) => s.brand_id)

  // Auto-expand parents with selected subplatforms
  const selectedSet = new Set(selectedPlatformIds.value)
  const parentsToExpand: string[] = []
  for (const platform of admin.platforms) {
    if (!platform.parent_platform_id) {
      const subs = admin.platforms.filter((p) => p.parent_platform_id === platform.id)
      if (subs.some((s) => selectedSet.has(s.id))) {
        parentsToExpand.push(platform.id)
      }
    }
  }
  expandedPlatformIds.value = parentsToExpand

  // Check for hash to open a specific brand or sector
  const hash = window.location.hash.slice(1)
  if (hash.startsWith('brand-')) {
    const slug = hash.slice(6)
    const brand = admin.brands.find((b) => b.slug === slug)
    if (brand) {
      selectedBrand.value = brand
      showBrandSlideover.value = true
    }
  } else if (hash.startsWith('sector-')) {
    const slug = hash.slice(7)
    const sector = admin.sectors.find((s) => s.slug === slug)
    if (sector) {
      selectedSector.value = sector
      showSectorSlideover.value = true
    }
  }

  isLoading.value = false
})

onUnmounted(() => {
  window.removeEventListener('hashchange', handleHashChange)
})

// Platforms - root platforms and subplatforms
const rootPlatforms = computed(() => admin.platforms.filter((p) => !p.parent_platform_id))

function isPlatformExpanded(id: string): boolean {
  return expandedPlatformIds.value.includes(id)
}

function togglePlatformExpand(id: string) {
  if (isPlatformExpanded(id)) {
    expandedPlatformIds.value = expandedPlatformIds.value.filter((i) => i !== id)
  } else {
    expandedPlatformIds.value = [...expandedPlatformIds.value, id]
  }
}

function isPlatformSelected(id: string): boolean {
  return selectedPlatformIds.value.includes(id)
}

function togglePlatform(id: string) {
  if (isPlatformSelected(id)) {
    selectedPlatformIds.value = selectedPlatformIds.value.filter((i) => i !== id)
    hiddenPlatformIds.value = hiddenPlatformIds.value.filter((i) => i !== id)
  } else {
    selectedPlatformIds.value = [...selectedPlatformIds.value, id]
  }
}

function isPlatformVisible(id: string): boolean {
  return !hiddenPlatformIds.value.includes(id)
}

function togglePlatformVisibility(id: string) {
  if (hiddenPlatformIds.value.includes(id)) {
    hiddenPlatformIds.value = hiddenPlatformIds.value.filter((i) => i !== id)
  } else {
    hiddenPlatformIds.value = [...hiddenPlatformIds.value, id]
  }
}

// Parent platform selection state: 'none' | 'partial' | 'all'
function getParentSelectionState(parentId: string): 'none' | 'partial' | 'all' {
  const subs = admin.getSubplatforms(parentId)
  if (subs.length === 0) {
    // No subplatforms, treat parent as a leaf
    return isPlatformSelected(parentId) ? 'all' : 'none'
  }
  const selectedCount = subs.filter((s) => isPlatformSelected(s.id)).length
  if (selectedCount === 0) return 'none'
  if (selectedCount === subs.length) return 'all'
  return 'partial'
}

// Countries
function isCountrySelected(id: string): boolean {
  return selectedCountryIds.value.includes(id)
}

function toggleCountry(id: string) {
  if (isCountrySelected(id)) {
    selectedCountryIds.value = selectedCountryIds.value.filter((i) => i !== id)
    hiddenCountryIds.value = hiddenCountryIds.value.filter((i) => i !== id)
  } else {
    selectedCountryIds.value = [...selectedCountryIds.value, id]
  }
}

function isCountryVisible(id: string): boolean {
  return !hiddenCountryIds.value.includes(id)
}

function toggleCountryVisibility(id: string) {
  if (hiddenCountryIds.value.includes(id)) {
    hiddenCountryIds.value = hiddenCountryIds.value.filter((i) => i !== id)
  } else {
    hiddenCountryIds.value = [...hiddenCountryIds.value, id]
  }
}

function selectAllInGroup(groupId: string) {
  const group = admin.countryGroups.find((g) => g.id === groupId)
  if (!group) return
  const newIds = new Set(selectedCountryIds.value)
  group.country_ids.forEach((id) => newIds.add(id))
  selectedCountryIds.value = Array.from(newIds)
}

function deselectAllInGroup(groupId: string) {
  const group = admin.countryGroups.find((g) => g.id === groupId)
  if (!group) return
  const groupIds = new Set(group.country_ids)
  selectedCountryIds.value = selectedCountryIds.value.filter((id) => !groupIds.has(id))
  hiddenCountryIds.value = hiddenCountryIds.value.filter((id) => !groupIds.has(id))
}

function showAllInGroup(groupId: string) {
  const group = admin.countryGroups.find((g) => g.id === groupId)
  if (!group) return
  const groupIds = new Set(group.country_ids)
  hiddenCountryIds.value = hiddenCountryIds.value.filter((id) => !groupIds.has(id))
}

function hideAllInGroup(groupId: string) {
  const group = admin.countryGroups.find((g) => g.id === groupId)
  if (!group) return
  const selectedInGroup = group.country_ids.filter((id) => selectedCountryIds.value.includes(id))
  const newHidden = new Set(hiddenCountryIds.value)
  selectedInGroup.forEach((id) => newHidden.add(id))
  hiddenCountryIds.value = Array.from(newHidden)
}

function getGroupVisibilityState(groupId: string): 'all' | 'partial' | 'none' {
  const group = admin.countryGroups.find((g) => g.id === groupId)
  if (!group) return 'none'
  const selectedInGroup = group.country_ids.filter((id) => selectedCountryIds.value.includes(id))
  if (selectedInGroup.length === 0) return 'none'
  const visibleInGroup = selectedInGroup.filter((id) => !hiddenCountryIds.value.includes(id))
  if (visibleInGroup.length === 0) return 'none'
  if (visibleInGroup.length === selectedInGroup.length) return 'all'
  return 'partial'
}

// Counters: collected vs displayed
const platformsCollected = computed(() => selectedPlatformIds.value.length)
const platformsDisplayed = computed(
  () => selectedPlatformIds.value.filter((id) => !hiddenPlatformIds.value.includes(id)).length,
)
const countriesCollected = computed(() => selectedCountryIds.value.length)
const countriesDisplayed = computed(
  () => selectedCountryIds.value.filter((id) => !hiddenCountryIds.value.includes(id)).length,
)
const brandsCollected = computed(() => selectedBrandIds.value.length)
const brandsDisplayed = computed(
  () => selectedBrandIds.value.filter((id) => !hiddenBrandIds.value.includes(id)).length,
)

const groupCoverage = computed(() => {
  const groups = admin.countryGroups.map((group) => {
    const selectedInGroup = group.country_ids.filter((id) => selectedCountryIds.value.includes(id))
    return {
      ...group,
      selectedCount: selectedInGroup.length,
      totalCount: group.country_ids.length,
    }
  })

  if (showOnlySelectedCountries.value) {
    return groups.filter((g) => g.selectedCount > 0)
  }

  return groups
})

// Brands
function isBrandSelected(id: string): boolean {
  return selectedBrandIds.value.includes(id)
}

function toggleBrand(id: string) {
  if (isBrandSelected(id)) {
    selectedBrandIds.value = selectedBrandIds.value.filter((i) => i !== id)
    // Also remove from owned and hidden if unselecting
    ownedBrandIds.value = ownedBrandIds.value.filter((i) => i !== id)
    hiddenBrandIds.value = hiddenBrandIds.value.filter((i) => i !== id)
  } else {
    selectedBrandIds.value = [...selectedBrandIds.value, id]
  }
}

function isBrandOwned(id: string): boolean {
  return ownedBrandIds.value.includes(id)
}

function toggleBrandOwnershipLocal(id: string) {
  if (isBrandOwned(id)) {
    ownedBrandIds.value = ownedBrandIds.value.filter((i) => i !== id)
  } else {
    ownedBrandIds.value = [...ownedBrandIds.value, id]
  }
}

function isBrandVisible(id: string): boolean {
  return !hiddenBrandIds.value.includes(id)
}

function toggleBrandVisibility(id: string) {
  if (hiddenBrandIds.value.includes(id)) {
    hiddenBrandIds.value = hiddenBrandIds.value.filter((i) => i !== id)
  } else {
    hiddenBrandIds.value = [...hiddenBrandIds.value, id]
  }
}

// Sector filter options (including "No sector")
const sectorFilterOptions = computed(() => [
  { label: 'No sector', value: NO_SECTOR },
  ...admin.sectors.map((s) => ({
    label: s.name,
    value: s.id,
    icon: s.emoji,
  })),
])

// Sectors that have at least one selected brand (for soft highlight)
const sectorsWithSelectedBrands = computed(() => {
  const sectorIds = new Set<string>()
  for (const brand of admin.brands) {
    if (selectedBrandIds.value.includes(brand.id)) {
      if (brand.sectors.length === 0) {
        sectorIds.add(NO_SECTOR)
      } else {
        for (const sector of brand.sectors) {
          sectorIds.add(sector.id)
        }
      }
    }
  }
  return sectorIds
})

// Platform filter options (root platforms only)
const platformFilterOptions = computed(() => {
  return admin.platforms
    .filter((p) => !p.parent_platform_id)
    .map((p) => ({
      value: p.code,
      label: formatPlatformName(p.name),
    }))
})

// Filtered brands for display (search + sector filter + selected only toggle)
const filteredBrands = computed(() => {
  let result = admin.brands

  // Filter by "selected only" toggle
  if (showOnlySelectedBrands.value) {
    result = result.filter((b) => selectedBrandIds.value.includes(b.id))
  }

  // Filter by search query
  if (brandSearchQuery.value) {
    const query = brandSearchQuery.value.toLowerCase()
    result = result.filter((b) => b.name.toLowerCase().includes(query))
  }

  // Filter by included sectors (OR - must have ANY selected)
  if (sectorFilter.included.value.length > 0) {
    result = result.filter((b) => {
      const brandSectorIds = b.sectors.map((s) => s.id)
      return sectorFilter.included.value.some((sectorId) => {
        if (sectorId === NO_SECTOR) return b.sectors.length === 0
        return brandSectorIds.includes(sectorId)
      })
    })
  }

  // Filter by excluded sectors (AND - must NOT have ANY excluded)
  if (sectorFilter.excluded.value.length > 0) {
    result = result.filter((b) => {
      const brandSectorIds = b.sectors.map((s) => s.id)
      return sectorFilter.excluded.value.every((sectorId) => {
        if (sectorId === NO_SECTOR) return b.sectors.length > 0
        return !brandSectorIds.includes(sectorId)
      })
    })
  }

  // Filter by platforms missing advertisers (AND - must be missing ALL excluded, including subplatforms)
  if (platformFilter.excluded.value.length > 0) {
    result = result.filter((b) =>
      platformFilter.excluded.value.every((rootCode) => {
        const familyCodes = admin.getPlatformFamilyCodes(rootCode)
        return !familyCodes.some((code) => b.platform_codes.includes(code))
      }),
    )
  }

  // Filter by platforms that have advertisers (AND - must have ALL included, checking subplatforms)
  if (platformFilter.included.value.length > 0) {
    result = result.filter((b) =>
      platformFilter.included.value.every((rootCode) => {
        const familyCodes = admin.getPlatformFamilyCodes(rootCode)
        return familyCodes.some((code) => b.platform_codes.includes(code))
      }),
    )
  }

  return result
})

// Filtered countries (search by name or code + selected only toggle)
const filteredCountries = computed(() => {
  let result = admin.countries

  if (showOnlySelectedCountries.value) {
    result = result.filter((c) => selectedCountryIds.value.includes(c.id))
  }

  if (countrySearchQuery.value) {
    const q = countrySearchQuery.value.toLowerCase()
    result = result.filter(
      (c) => c.name_en.toLowerCase().includes(q) || c.code.toLowerCase().includes(q),
    )
  }

  return result
})

// Filtered root platforms (selected only toggle)
const filteredRootPlatforms = computed(() => {
  if (!showOnlySelectedPlatforms.value) return rootPlatforms.value

  return rootPlatforms.value.filter((p) => {
    const subs = admin.getSubplatforms(p.id)
    if (subs.length === 0) {
      return isPlatformSelected(p.id)
    }
    return subs.some((s) => isPlatformSelected(s.id))
  })
})

function getFilteredSubplatforms(parentId: string) {
  const subs = admin.getSubplatforms(parentId)
  if (!showOnlySelectedPlatforms.value) return subs
  return subs.filter((s) => isPlatformSelected(s.id))
}

// Create brand handler
async function handleCreateBrand(data: { name: string; slug?: string; logo_file?: File }) {
  try {
    const brand = await admin.createBrand({ name: data.name, slug: data.slug })
    if (data.logo_file) {
      await admin.saveBrandLogo(brand.id, data.logo_file)
    }
    await admin.fetchBrands()
    selectedBrandIds.value = [...selectedBrandIds.value, brand.id]
    showCreateBrandModal.value = false
    // Open brand detail slideover
    const createdBrand = admin.brands.find((b) => b.id === brand.id)
    if (createdBrand) {
      openBrandSettings(createdBrand)
    }
  } catch (error) {
    handleError(error, 'Failed to create brand')
  }
}

function handleBrandUpdated(brand: Brand) {
  selectedBrand.value = brand
}

function openBrandSettings(brand: Brand) {
  const isReplacing = showBrandSlideover.value
  selectedBrand.value = brand
  showBrandSlideover.value = true
  if (isReplacing) {
    history.replaceState(null, '', `#brand-${brand.slug}`)
  } else {
    history.pushState(null, '', `#brand-${brand.slug}`)
  }
}

function closeBrandSlideover() {
  showBrandSlideover.value = false
  selectedBrand.value = null
}

function openSectorSettings(sectorId: string) {
  const sector = admin.sectors.find((s) => s.id === sectorId)
  if (sector) {
    const isReplacing = showSectorSlideover.value
    selectedSector.value = sector
    showSectorSlideover.value = true
    if (isReplacing) {
      history.replaceState(null, '', `#sector-${sector.slug}`)
    } else {
      history.pushState(null, '', `#sector-${sector.slug}`)
    }
  }
}

function closeSectorSlideover() {
  showSectorSlideover.value = false
  selectedSector.value = null
}

function handleSectorUpdated(sector: Sector) {
  selectedSector.value = sector
}

function handleSectorDeleted() {
  if (selectedSector.value) {
    sectorFilter.remove(selectedSector.value.id)
  }
  closeSectorSlideover()
}

function handleHashChange() {
  const hash = window.location.hash.slice(1)
  if (!hash) {
    // Close any open slideover
    if (showBrandSlideover.value) {
      showBrandSlideover.value = false
      selectedBrand.value = null
    }
    if (showSectorSlideover.value) {
      showSectorSlideover.value = false
      selectedSector.value = null
    }
  } else if (hash.startsWith('brand-')) {
    const slug = hash.slice(6)
    if (selectedBrand.value?.slug !== slug) {
      const brand = admin.brands.find((b) => b.slug === slug)
      if (brand) {
        openBrandSettings(brand)
      } else {
        handleError(new Error('Brand not found'), 'Could not find brand')
        history.replaceState(null, '', window.location.pathname)
      }
    }
  } else if (hash.startsWith('sector-')) {
    const slug = hash.slice(7)
    if (selectedSector.value?.slug !== slug) {
      const sector = admin.sectors.find((s) => s.slug === slug)
      if (sector) {
        openSectorSettings(sector.id)
      } else {
        handleError(new Error('Sector not found'), 'Could not find sector')
        history.replaceState(null, '', window.location.pathname)
      }
    }
  }
}

// Clear hash when slideover closes
watch(showBrandSlideover, (isOpen) => {
  if (!isOpen && window.location.hash.startsWith('#brand-')) {
    history.replaceState(null, '', window.location.pathname)
  }
})

watch(showSectorSlideover, (isOpen) => {
  if (!isOpen && window.location.hash.startsWith('#sector-')) {
    history.replaceState(null, '', window.location.pathname)
  }
})

// Fetch advertisers and import stats when switching to table view
watch(brandsViewMode, async (mode) => {
  if (mode === 'table' && !tableDataLoaded.value) {
    isLoadingTableData.value = true
    try {
      const [advertisers, importStats] = await Promise.all([
        admin.fetchAllAdvertisers(),
        imports.fetchAllBrandImportStats(),
      ])
      brandAdvertisersMap.value = advertisers
      brandImportStatsMap.value = importStats
      tableDataLoaded.value = true
    } catch (err) {
      handleError(err, 'Failed to fetch table data')
    } finally {
      isLoadingTableData.value = false
    }
  }
})

// Save all
const hasChanges = computed(() => {
  const currentPlatforms = new Set(platformSubs.value.map((s) => s.platform_id))
  const currentCountries = new Set(countrySubs.value.map((s) => s.country_id))
  const currentBrands = new Set(brandSubs.value.map((s) => s.brand_id))
  const currentOwnedBrands = new Set(
    brandSubs.value.filter((s) => s.is_owner).map((s) => s.brand_id),
  )
  const currentHiddenPlatforms = new Set(
    platformSubs.value.filter((s) => !s.is_visible).map((s) => s.platform_id),
  )
  const currentHiddenCountries = new Set(
    countrySubs.value.filter((s) => !s.is_visible).map((s) => s.country_id),
  )
  const currentHiddenBrands = new Set(
    brandSubs.value.filter((s) => !s.is_visible).map((s) => s.brand_id),
  )

  const selectedPlatforms = new Set(selectedPlatformIds.value)
  const selectedCountries = new Set(selectedCountryIds.value)
  const selectedBrands = new Set(selectedBrandIds.value)
  const selectedOwnedBrands = new Set(ownedBrandIds.value)
  const selectedHiddenPlatforms = new Set(hiddenPlatformIds.value)
  const selectedHiddenCountries = new Set(hiddenCountryIds.value)
  const selectedHiddenBrands = new Set(hiddenBrandIds.value)

  const setsEqual = (a: Set<string>, b: Set<string>) => {
    if (a.size !== b.size) return false
    for (const item of a) if (!b.has(item)) return false
    return true
  }

  return (
    !setsEqual(currentPlatforms, selectedPlatforms) ||
    !setsEqual(currentCountries, selectedCountries) ||
    !setsEqual(currentBrands, selectedBrands) ||
    !setsEqual(currentOwnedBrands, selectedOwnedBrands) ||
    !setsEqual(currentHiddenPlatforms, selectedHiddenPlatforms) ||
    !setsEqual(currentHiddenCountries, selectedHiddenCountries) ||
    !setsEqual(currentHiddenBrands, selectedHiddenBrands)
  )
})

function discardChanges() {
  selectedPlatformIds.value = platformSubs.value.map((s) => s.platform_id)
  selectedCountryIds.value = countrySubs.value.map((s) => s.country_id)
  selectedBrandIds.value = brandSubs.value.map((s) => s.brand_id)
  ownedBrandIds.value = brandSubs.value.filter((s) => s.is_owner).map((s) => s.brand_id)
  hiddenPlatformIds.value = platformSubs.value
    .filter((s) => !s.is_visible)
    .map((s) => s.platform_id)
  hiddenCountryIds.value = countrySubs.value.filter((s) => !s.is_visible).map((s) => s.country_id)
  hiddenBrandIds.value = brandSubs.value.filter((s) => !s.is_visible).map((s) => s.brand_id)
}

async function saveAll() {
  if (!organization.value || isSaving.value) return
  isSaving.value = true
  try {
    const platformSubscriptions = selectedPlatformIds.value.map((id) => ({
      platform_id: id,
      is_visible: !hiddenPlatformIds.value.includes(id),
    }))
    const countrySubscriptions = selectedCountryIds.value.map((id) => ({
      country_id: id,
      is_visible: !hiddenCountryIds.value.includes(id),
    }))
    const brandSubscriptions = selectedBrandIds.value.map((id) => ({
      brand_id: id,
      is_owner: ownedBrandIds.value.includes(id),
      is_visible: !hiddenBrandIds.value.includes(id),
    }))
    const [platforms, countries, brands] = await Promise.all([
      admin.updateOrgPlatformSubscriptions(organization.value.id, platformSubscriptions),
      admin.updateOrgCountrySubscriptions(organization.value.id, countrySubscriptions),
      admin.updateOrgBrandSubscriptions(organization.value.id, brandSubscriptions),
    ])
    platformSubs.value = platforms
    countrySubs.value = countries
    brandSubs.value = brands
    ownedBrandIds.value = brands.filter((s) => s.is_owner).map((s) => s.brand_id)
    hiddenPlatformIds.value = platforms.filter((s) => !s.is_visible).map((s) => s.platform_id)
    hiddenCountryIds.value = countries.filter((s) => !s.is_visible).map((s) => s.country_id)
    hiddenBrandIds.value = brands.filter((s) => !s.is_visible).map((s) => s.brand_id)
  } catch (error) {
    handleError(error, 'Failed to save subscriptions')
  } finally {
    isSaving.value = false
  }
}
</script>

<template>
  <AppComponent :title="`Setup - ${organization?.name || ''}`" id="admin-org-setup">
    <!-- Floating save button -->
    <div v-if="!isLoading && organization" class="fixed top-4 right-6 z-50 flex gap-2">
      <UButton
        v-if="hasChanges"
        color="neutral"
        variant="ghost"
        :disabled="isSaving"
        @click="discardChanges"
      >
        Discard changes
      </UButton>
      <UButton
        color="primary"
        :loading="isSaving"
        :disabled="!hasChanges"
        :variant="hasChanges ? 'solid' : 'soft'"
        :class="{ 'animate-pulse': hasChanges }"
        @click="saveAll"
      >
        {{ hasChanges ? 'Save changes' : 'Saved' }}
      </UButton>
    </div>

    <div class="flex flex-col gap-6">
      <div v-if="isLoading" class="space-y-6">
        <USkeleton class="h-32 w-full" />
        <USkeleton class="h-48 w-full" />
        <USkeleton class="h-32 w-full" />
      </div>

      <div v-else-if="!organization" class="text-center py-12 text-muted">
        Organization not found
      </div>

      <template v-else>
        <!-- Platforms Section -->
        <section>
          <div class="flex items-center gap-3 mb-1">
            <h2 class="text-lg font-semibold">Platforms</h2>
            <LabeledSwitch
              v-model="showOnlySelectedPlatforms"
              label="Selected only"
              class="ml-auto"
            />
          </div>
          <p class="text-sm text-muted mb-3">
            <span class="font-medium">{{ platformsCollected }}</span> collected ·
            <span class="font-medium">{{ platformsDisplayed }}</span> displayed
          </p>

          <UPageCard variant="subtle">
            <div class="flex flex-wrap gap-2">
              <template v-for="platform in filteredRootPlatforms" :key="platform.id">
                <!-- Parent platform with subplatforms -->
                <template v-if="getFilteredSubplatforms(platform.id).length > 0">
                  <UButton
                    :color="getParentSelectionState(platform.id) === 'none' ? 'neutral' : 'primary'"
                    :variant="getParentSelectionState(platform.id) === 'all' ? 'solid' : 'soft'"
                    size="xs"
                    @click="togglePlatformExpand(platform.id)"
                  >
                    {{ formatPlatformName(platform.name) }}
                    <UIcon
                      :name="
                        isPlatformExpanded(platform.id)
                          ? 'i-lucide-chevron-up'
                          : 'i-lucide-chevron-down'
                      "
                      class="ml-1"
                    />
                  </UButton>
                </template>
                <!-- Leaf platform (no subplatforms) -->
                <template v-else>
                  <div class="group relative inline-flex">
                    <UButton
                      :color="isPlatformSelected(platform.id) ? 'primary' : 'neutral'"
                      :variant="isPlatformSelected(platform.id) ? 'solid' : 'soft'"
                      size="xs"
                      :class="{
                        'opacity-50':
                          isPlatformSelected(platform.id) && !isPlatformVisible(platform.id),
                      }"
                      @click="togglePlatform(platform.id)"
                    >
                      {{ formatPlatformName(platform.name) }}
                    </UButton>
                    <button
                      v-if="isPlatformSelected(platform.id)"
                      class="absolute -right-1 -top-1 p-0.5 rounded-full bg-elevated border border-default shadow-sm"
                      :class="{
                        'opacity-0 group-hover:opacity-100': isPlatformVisible(platform.id),
                        'opacity-100': !isPlatformVisible(platform.id),
                      }"
                      @click.stop="togglePlatformVisibility(platform.id)"
                    >
                      <UIcon
                        :name="isPlatformVisible(platform.id) ? 'i-lucide-eye' : 'i-lucide-eye-off'"
                        class="w-3 h-3 text-muted"
                      />
                    </button>
                  </div>
                </template>
              </template>
            </div>

            <!-- Expanded subplatforms -->
            <template v-for="platform in filteredRootPlatforms" :key="`subs-${platform.id}`">
              <div
                v-if="
                  isPlatformExpanded(platform.id) && getFilteredSubplatforms(platform.id).length > 0
                "
                class="mt-3 pt-3 border-t border-default"
              >
                <div class="text-sm font-medium mb-2">{{ formatPlatformName(platform.name) }}</div>
                <div class="flex flex-wrap gap-2">
                  <div
                    v-for="sub in getFilteredSubplatforms(platform.id)"
                    :key="sub.id"
                    class="group relative inline-flex"
                  >
                    <UButton
                      :color="isPlatformSelected(sub.id) ? 'primary' : 'neutral'"
                      :variant="isPlatformSelected(sub.id) ? 'solid' : 'soft'"
                      size="xs"
                      :class="{
                        'opacity-50': isPlatformSelected(sub.id) && !isPlatformVisible(sub.id),
                      }"
                      @click="togglePlatform(sub.id)"
                    >
                      {{ formatPlatformName(sub.name) }}
                    </UButton>
                    <button
                      v-if="isPlatformSelected(sub.id)"
                      class="absolute -right-1 -top-1 p-0.5 rounded-full bg-elevated border border-default shadow-sm"
                      :class="{
                        'opacity-0 group-hover:opacity-100': isPlatformVisible(sub.id),
                        'opacity-100': !isPlatformVisible(sub.id),
                      }"
                      @click.stop="togglePlatformVisibility(sub.id)"
                    >
                      <UIcon
                        :name="isPlatformVisible(sub.id) ? 'i-lucide-eye' : 'i-lucide-eye-off'"
                        class="w-3 h-3 text-muted"
                      />
                    </button>
                  </div>
                </div>
              </div>
            </template>
          </UPageCard>
        </section>

        <!-- Countries Section -->
        <section>
          <div class="flex items-center gap-3 mb-1">
            <h2 class="text-lg font-semibold">Countries</h2>
            <LabeledSwitch
              v-model="showOnlySelectedCountries"
              label="Selected only"
              class="ml-auto"
            />
          </div>
          <p class="text-sm text-muted mb-3">
            <span class="font-medium">{{ countriesCollected }}</span> collected ·
            <span class="font-medium">{{ countriesDisplayed }}</span> displayed
          </p>

          <UPageCard variant="subtle">
            <!-- Regions subheader -->
            <h3 class="text-sm font-medium mb-1">Regions</h3>
            <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
              <div
                v-for="group in groupCoverage"
                :key="group.id"
                class="group/region flex items-center gap-2 text-sm hover:bg-muted/50 px-2 py-1 rounded"
                :class="{
                  'bg-primary/10': group.selectedCount === group.totalCount && group.totalCount > 0,
                }"
              >
                <label class="flex items-center gap-2 cursor-pointer flex-1">
                  <input
                    type="checkbox"
                    :checked="group.selectedCount === group.totalCount && group.totalCount > 0"
                    :indeterminate="
                      group.selectedCount > 0 && group.selectedCount < group.totalCount
                    "
                    class="rounded border-gray-300 text-primary focus:ring-primary h-3 w-3"
                    @change="
                      group.selectedCount === group.totalCount
                        ? deselectAllInGroup(group.id)
                        : selectAllInGroup(group.id)
                    "
                  />
                  <span>{{ group.name }}</span>
                </label>
                <button
                  v-if="group.selectedCount > 0"
                  class="p-0.5 rounded"
                  :class="{
                    'opacity-0 group-hover/region:opacity-100':
                      getGroupVisibilityState(group.id) === 'all',
                    'opacity-100': getGroupVisibilityState(group.id) !== 'all',
                  }"
                  @click="
                    getGroupVisibilityState(group.id) === 'all'
                      ? hideAllInGroup(group.id)
                      : showAllInGroup(group.id)
                  "
                >
                  <UIcon
                    :name="
                      getGroupVisibilityState(group.id) === 'all'
                        ? 'i-lucide-eye'
                        : 'i-lucide-eye-off'
                    "
                    class="w-3.5 h-3.5 text-muted"
                  />
                </button>
                <span class="text-xs text-muted"
                  >{{ group.selectedCount }}/{{ group.totalCount }}</span
                >
              </div>
            </div>

            <!-- Countries subheader + search -->
            <div class="mt-4 pt-3 border-t border-default">
              <h3 class="text-sm font-medium mb-4">Countries</h3>
              <UInput
                v-model="countrySearchQuery"
                placeholder="Search by name or code..."
                icon="i-lucide-search"
                :ui="countrySearchQuery ? activeFilterUi : undefined"
                size="sm"
                class="max-w-xs"
              />
            </div>
            <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
              <div
                v-for="country in filteredCountries"
                :key="country.id"
                class="group/country flex items-center gap-2 px-2 py-1 rounded text-sm hover:bg-muted/50"
              >
                <label class="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                  <input
                    type="checkbox"
                    :checked="isCountrySelected(country.id)"
                    class="rounded border-gray-300 text-primary focus:ring-primary h-3 w-3 shrink-0"
                    @change="toggleCountry(country.id)"
                  />
                  <span class="shrink-0">{{ country.emoji }}</span>
                  <span
                    class="truncate"
                    :class="{
                      'opacity-50': isCountrySelected(country.id) && !isCountryVisible(country.id),
                    }"
                    >{{ country.name_en }}</span
                  >
                </label>
                <button
                  v-if="isCountrySelected(country.id)"
                  class="p-0.5 rounded shrink-0"
                  :class="{
                    'opacity-0 group-hover/country:opacity-100': isCountryVisible(country.id),
                    'opacity-100': !isCountryVisible(country.id),
                  }"
                  @click="toggleCountryVisibility(country.id)"
                >
                  <UIcon
                    :name="isCountryVisible(country.id) ? 'i-lucide-eye' : 'i-lucide-eye-off'"
                    class="w-3.5 h-3.5 text-muted"
                  />
                </button>
              </div>
            </div>
          </UPageCard>
        </section>

        <!-- Brands Section -->
        <section>
          <div class="flex items-center gap-3 mb-1">
            <h2 class="text-lg font-semibold">Brands</h2>
            <div class="flex items-center gap-3 ml-auto">
              <SegmentedControl v-model="brandsViewMode" :options="viewModeOptions" />
              <LabeledSwitch v-model="showOnlySelectedBrands" label="Selected only" />
            </div>
          </div>
          <p class="text-sm text-muted mb-3">
            <span class="font-medium">{{ brandsCollected }}</span> collected ·
            <span class="font-medium">{{ brandsDisplayed }}</span> displayed
          </p>

          <!-- Search and Filter -->
          <div class="flex items-center gap-4 mb-3">
            <UInput
              v-model="brandSearchQuery"
              icon="i-lucide-search"
              placeholder="Search brands..."
              :ui="brandSearchQuery ? activeFilterUi : undefined"
              size="sm"
              class="w-64"
            />
          </div>
          <!-- Sector filter -->
          <div class="mb-3">
            <TriStateFilterButtons
              label="Filter by sector:"
              :options="sectorFilterOptions"
              :included="sectorFilter.included.value"
              :excluded="sectorFilter.excluded.value"
              :highlighted="sectorsWithSelectedBrands"
              size="xs"
              @toggle="sectorFilter.toggle"
              @clear="sectorFilter.clear"
            >
              <template #option-action="{ option }">
                <span
                  v-if="option.value !== NO_SECTOR"
                  class="ml-1 opacity-50 hover:opacity-100 transition-opacity cursor-pointer"
                  @click.stop="openSectorSettings(option.value)"
                >
                  <UIcon name="i-lucide-settings" class="w-3 h-3" />
                </span>
              </template>
            </TriStateFilterButtons>
          </div>
          <!-- Platform filter -->
          <div v-if="platformFilterOptions.length > 0" class="mb-3">
            <TriStateFilterButtons
              label="Filter by platform:"
              :options="platformFilterOptions"
              :included="platformFilter.included.value"
              :excluded="platformFilter.excluded.value"
              @toggle="platformFilter.toggle"
              @clear="platformFilter.clear"
            />
          </div>

          <!-- Buttons View -->
          <UPageCard v-if="brandsViewMode === 'buttons'" variant="subtle">
            <!-- Legend -->
            <div class="flex items-center gap-4 mb-3 text-xs text-muted">
              <span class="flex items-center gap-1">
                <span class="w-3 h-3 rounded bg-[var(--ui-success)]"></span>
                <UIcon name="i-lucide-building-2" class="w-3 h-3 text-[var(--ui-success)]" />
                Org brand
              </span>
              <span class="flex items-center gap-1">
                <span class="w-3 h-3 rounded bg-[var(--ui-primary)]"></span>
                <UIcon name="i-lucide-target" class="w-3 h-3 text-[var(--ui-primary)]" />
                Tracked brand
              </span>
              <span class="text-muted/70">Click the icon on a selected brand to switch type</span>
            </div>
            <div v-if="filteredBrands.length === 0" class="text-center py-4 text-muted text-sm">
              {{
                showOnlySelectedBrands && !brandSearchQuery
                  ? 'No brands selected.'
                  : 'No brands match the filter.'
              }}
            </div>
            <div class="flex flex-wrap gap-2">
              <div
                v-for="brand in filteredBrands"
                :key="brand.id"
                class="group relative inline-flex"
              >
                <UButton
                  :color="
                    isBrandSelected(brand.id)
                      ? isBrandOwned(brand.id)
                        ? 'success'
                        : 'primary'
                      : 'neutral'
                  "
                  :variant="isBrandSelected(brand.id) ? 'solid' : 'soft'"
                  size="xs"
                  :class="{
                    'opacity-50': isBrandSelected(brand.id) && !isBrandVisible(brand.id),
                  }"
                  @click="toggleBrand(brand.id)"
                >
                  <UAvatar
                    v-if="brand.logo_url"
                    :src="brand.logo_url"
                    :alt="brand.name"
                    size="3xs"
                    class="mr-1"
                  />
                  {{ brand.name }}
                  <UTooltip
                    v-if="isBrandSelected(brand.id)"
                    :text="
                      isBrandOwned(brand.id)
                        ? 'Org brand (click to change)'
                        : 'Tracked brand (click to change)'
                    "
                  >
                    <span
                      class="ml-1 hover:opacity-80 transition-opacity cursor-pointer"
                      @click.stop="toggleBrandOwnershipLocal(brand.id)"
                    >
                      <UIcon
                        :name="isBrandOwned(brand.id) ? 'i-lucide-building-2' : 'i-lucide-target'"
                        :class="
                          isBrandOwned(brand.id) ? 'w-3 h-3 text-white' : 'w-3 h-3 text-white/70'
                        "
                      />
                    </span>
                  </UTooltip>
                  <span
                    class="ml-1 opacity-50 hover:opacity-100 transition-opacity cursor-pointer"
                    @click.stop="openBrandSettings(brand)"
                  >
                    <UIcon name="i-lucide-settings" class="w-3 h-3" />
                  </span>
                </UButton>
                <button
                  v-if="isBrandSelected(brand.id)"
                  class="absolute -right-1 -top-1 p-0.5 rounded-full bg-elevated border border-default shadow-sm"
                  :class="{
                    'opacity-0 group-hover:opacity-100': isBrandVisible(brand.id),
                    'opacity-100': !isBrandVisible(brand.id),
                  }"
                  @click.stop="toggleBrandVisibility(brand.id)"
                >
                  <UIcon
                    :name="isBrandVisible(brand.id) ? 'i-lucide-eye' : 'i-lucide-eye-off'"
                    class="w-3 h-3 text-muted"
                  />
                </button>
              </div>
              <UButton
                color="primary"
                variant="soft"
                size="xs"
                icon="i-lucide-plus"
                @click="showCreateBrandModal = true"
              >
                Create brand
              </UButton>
            </div>
          </UPageCard>

          <!-- Table View -->
          <UPageCard v-else variant="subtle">
            <!-- Legend -->
            <div class="flex items-center gap-4 mb-3 text-xs text-muted">
              <span class="flex items-center gap-1">
                <UIcon name="i-lucide-building-2" class="w-3 h-3 text-[var(--ui-success)]" />
                Org brand
              </span>
              <span class="flex items-center gap-1">
                <UIcon name="i-lucide-target" class="w-3 h-3 text-[var(--ui-primary)]" />
                Tracked brand
              </span>
              <span class="text-muted/70"
                >Click the Type column to switch between Org and Tracked</span
              >
            </div>
            <BrandsTable
              :brands="filteredBrands"
              :selected-brand-ids="selectedBrandIds"
              :owned-brand-ids="ownedBrandIds"
              :brand-advertisers="brandAdvertisersMap"
              :brand-import-stats="brandImportStatsMap"
              :loading="isLoadingTableData"
              @select="toggleBrand"
              @toggle-ownership="toggleBrandOwnershipLocal"
              @settings="openBrandSettings"
              @view-imports="
                (b) => router.push(`/admin/imports?search=${encodeURIComponent(b.name)}`)
              "
            />
            <div class="mt-3">
              <UButton
                color="primary"
                variant="soft"
                size="xs"
                icon="i-lucide-plus"
                @click="showCreateBrandModal = true"
              >
                Create brand
              </UButton>
            </div>
          </UPageCard>
        </section>

        <!-- Create Brand Modal -->
        <UModal v-model:open="showCreateBrandModal">
          <template #content>
            <UCard>
              <template #header>
                <h3 class="text-lg font-semibold">Create Brand</h3>
              </template>
              <AdminBrandForm
                :brand="null"
                @submit="handleCreateBrand"
                @cancel="showCreateBrandModal = false"
              />
            </UCard>
          </template>
        </UModal>

        <!-- Brand Detail Slideover -->
        <USlideover v-model:open="showBrandSlideover" side="right">
          <template #content>
            <AdminBrandDetail
              :brand="selectedBrand"
              @close="closeBrandSlideover"
              @deleted="closeBrandSlideover"
              @updated="handleBrandUpdated"
            />
          </template>
        </USlideover>

        <!-- Sector Detail Slideover -->
        <USlideover v-model:open="showSectorSlideover" side="right">
          <template #content>
            <AdminSectorDetail
              :sector="selectedSector"
              @close="closeSectorSlideover"
              @deleted="handleSectorDeleted"
              @updated="handleSectorUpdated"
            />
          </template>
        </USlideover>
      </template>
    </div>
  </AppComponent>
</template>
